import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'app/profile/services/user.service';
import { Chart, ChartDataSets } from 'chart.js';
import * as moment from 'moment';
import { zip } from 'rxjs';
import {finalize} from 'rxjs/operators';

import { getTranslation } from 'shared/helpers/get-translation';

import { NavService } from 'shared/nav/nav.service';
import { Entry, WeekData } from '../../model/week-data';
import { WeekService } from '../../services/week.service';

const LABEL_FORMAT: string = 'D.M.';

@Component({
  selector: 'performance-panel',
  templateUrl: 'performance-panel.html',
  styleUrls: ['performance-panel.scss']})
export class PerformancePanel implements AfterViewInit {

  @ViewChild('chartCanvas', {static: true}) private _canvasRef: ElementRef;

  private _chart: Chart;
  private _datasetLabels: Array<string>;
  private _loadingData: boolean;

  private _maxLabelCount: number = 3;

  private _datasetMorningIndex: number = 0;
  private _datasetEveningIndex: number = 1;
  private _datasetPerformanceIndex: number = 2;

  constructor(
    public navService: NavService,
    public translateService: TranslateService,
    public weekService: WeekService,
    public userService: UserService) { }

  private static createEmptyData(): Array<number> {
    return [null, null, null, null, null, null, null];
  }

  private static createLabels(start: Date): Array<string> {
    const date: moment.Moment = moment(start);

    const labels: Array<string> = [date.format(LABEL_FORMAT)];

    const entryCount: number = 6;

    for (let i: number = 0; i < entryCount; i++) {
      date.add(1, 'day');
      labels.push(date.format(LABEL_FORMAT));
    }

    return labels;
  }

  public get loadingData(): boolean {
    return this._loadingData;
  }

  public ngAfterViewInit(): void {
    this._loadingData = true;

    const startDate: Date = moment().subtract(1, 'week')
      .add(1, 'day')
      .toDate();

    zip(
      this.translateService.get('WEEK.LABEL_ENERGYLEVEL_MORNING'),
      this.translateService.get('WEEK.LABEL_ENERGYLEVEL_EVENING'),
      this.translateService.get('WEEK.LABEL_PERFORMANCE'),
    ).subscribe((translations: Array<string>) => {
      this._datasetLabels = translations;
      this.initChart(startDate);
      this.loadData();
    });
  }

  private loadData(): void {
    this._loadingData = true;

    this.weekService.getWeekData()
      .pipe(finalize(() => { this._loadingData = false; }))
      .subscribe((data: WeekData) => {
        const datasets: Array<ChartDataSets> = this._chart.data['datasets'];

        const morning: Array<number> = data.energylevelMorning.map(this.mapEntryToValue);
        const morningDataSet: ChartDataSets = datasets[this._datasetMorningIndex];
        morningDataSet.data = morning;

        const evening: Array<number> = data.energylevelEvening.map(this.mapEntryToValue);
        const eveningDataSet: ChartDataSets = datasets[this._datasetEveningIndex];
        eveningDataSet.data = evening;

        const performance: Array<number> = data.performancelevel.map(this.mapEntryToValue);
        const performanceDataSet: ChartDataSets = datasets[this._datasetPerformanceIndex];
        performanceDataSet.data = performance;

        this._chart.update();
      });
  }

  private mapEntryToValue(entry: Entry): number {
    if (!entry) {
      return null;
    }
    return entry.value;
  }

  private initChart(start: Date): void {

    const canvas: HTMLCanvasElement = this._canvasRef.nativeElement;
    const isDeka = false; //(this.userService.getTheme() === 'deka' || this.userService.getTheme() === 'fcb');
    let fontColor = 'white';
    let fontColor05 = 'rgba(255, 255, 255, 0.5)';
    try {
      fontColor = getComputedStyle(document.getElementsByClassName('ion-page')[0]).getPropertyValue('--app-text-color');
      fontColor05 = getComputedStyle(document.getElementsByClassName('ion-page')[0]).getPropertyValue('--app-text-color05');
    } catch (e) {
      // ignore
    }
    this._chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: PerformancePanel.createLabels(start),
        datasets: [
          {
            type: 'line',
            label: this._datasetLabels[this._datasetMorningIndex],
            backgroundColor: 'rgba(206, 116, 55, ' + (isDeka ? 1.0 : 0.5) + ')',
            borderColor: 'rgba(206, 116, 55, 1)',
            pointBackgroundColor: 'rgba(206, 116, 55, 1)',
            pointBorderColor: 'white',
            pointRadius: 4,
            borderWidth: 1,
          },
          {
            type: 'line',
            label: this._datasetLabels[this._datasetEveningIndex],
            backgroundColor: 'rgba(106, 168, 67, ' + (isDeka ? 1.0 : 0.25) + ')',
            borderColor: 'rgba(106, 168, 67, 1)',
            pointBackgroundColor: 'rgba(106, 168, 67, 1)',
            pointBorderColor: 'white',
            pointRadius: 4,
            borderWidth: 1,
          },
          {
            type: 'bar',
            label: this._datasetLabels[this._datasetPerformanceIndex],
            backgroundColor: 'rgba(40, 98, 191, ' + (isDeka ? 1.0 : 0.5) + ')',
            borderColor: 'rgba(0, 0, 0, 0)',
            borderWidth: 1,
          },
        ],
      },
      options: {
        legend: {
          position: 'bottom',
          labels: {
            fontColor: fontColor,
            boxWidth: 16,
          },
          onClick: (event, item) => { event.stopImmediatePropagation(); },
        },
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          yAxes: [
            {
              id: 'y-axis-0',
              position: 'left',
              display: true,
              ticks: {
                min: 0,
                max: 100,
                fontColor: fontColor,
              },
              gridLines: {
                color: fontColor05,
                zeroLineColor: fontColor05,
                drawBorder: false,
              },
            },
            {
              id: 'y-axis-1',
              position: 'right',
              display: true,
              ticks: {
                min: 0,
                max: 100,
                fontColor: fontColor,
              },
              gridLines: {
                color: fontColor05,
                zeroLineColor: fontColor05,
                drawBorder: false,
              },
            },
            // tslint:disable-next-line:no-any
          ] as Array<any>,
          xAxes: [{
            ticks: {
              fontColor: fontColor,
            },
            gridLines: {
              display: false,
              drawTicks: false,
            },
            // tslint:disable-next-line:no-any
          }] as Array<any>,
        },
      },
    });
  }
}
